import { useCallback, useEffect, useState } from 'react';

import styleGuideApi from '@app/api/styleGuide';
import useDispatch from '@app/hooks/useDispatch';
import assertIsNever from '@app/utils/assertIsNever';

import useUploadStyleGuide from '../../hooks/useUploadStyleguide/useUploadStyleGuide';
import { isFileUploadRemoved } from '../../model/fileUpload';
import AssignLanguageWizard from './AssignLanguageWizard';
import UploadWizard from './UploadWizard';

type WizardStep = 'idle' | 'upload' | 'assignLanguage';

export const UploadStyleGuideWizard = () => {
    const dispatch = useDispatch();
    const [wizardStep, setWizardStep] = useState<WizardStep>('idle');

    const {
        uploads,
        removeUpload,
        resetStyleGuideUploads,
        stageSuccessfulUploads,
        changeStyleGuideName,
        assignLanguage,
        completeUpload,
    } = useUploadStyleGuide();

    useEffect(() => {
        if (wizardStep === 'idle' && uploads !== null) {
            setWizardStep('upload');
        }
    }, [uploads, wizardStep]);

    const onStyleGuidesSubmitted = useCallback(async () => {
        const response = await completeUpload();

        if (response !== null && 'error' in response) {
            return response;
        }

        dispatch(styleGuideApi.util.invalidateTags(['StyleGuides']));
        setWizardStep('idle');

        return response;
    }, [completeUpload, dispatch]);

    const onCancel = async () => {
        if (uploads === null) {
            return;
        }
        const cancel = uploads.map((upload) => {
            if (!isFileUploadRemoved(upload)) {
                return removeUpload(upload);
            }

            return Promise.resolve();
        });
        await Promise.all(cancel);

        dispatch(styleGuideApi.util.invalidateTags(['StyleGuides']));

        resetStyleGuideUploads();
        setWizardStep('idle');
    };

    switch (wizardStep) {
        case 'idle':
            return null;

        case 'upload':
            return (
                <UploadWizard
                    fileUploads={uploads}
                    onUploadRemoved={removeUpload}
                    onAssignFiles={() => {
                        stageSuccessfulUploads();
                        setWizardStep('assignLanguage');
                    }}
                    onCancel={() => onCancel()}
                />
            );

        case 'assignLanguage':
            return (
                <AssignLanguageWizard
                    fileUploads={uploads ?? []}
                    onUploadRemoved={removeUpload}
                    onNameChanged={changeStyleGuideName}
                    onLanguageAssigned={assignLanguage}
                    onStyleGuidesSubmitted={onStyleGuidesSubmitted}
                    onBackToUploads={() => setWizardStep('upload')}
                    onCancel={() => onCancel()}
                />
            );

        default:
            return assertIsNever(wizardStep);
    }
};

export default UploadStyleGuideWizard;
